import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { debounce } from '@lib/utils/throttle'

import { usePageHeader } from '@contexts/page-header-context'

import { Column } from '@components/ui-core/column/column'
import { Section } from '@components/ui-core/section/section'

import { type TypeTabbedSectionData } from '../../types'

import TabbedSectionContent from './tabbed-section-content'
import TabbedSectionHeader from './tabbed-section-header'

import styles from './tabbed-section.module.scss'

type TypeHeaderRefEl = HTMLDivElement | null
type TypeTabsRefEls = Record<string, HTMLDivElement | null>

/**
 * The page and section headers are both position: sticky,
 * so we need to calculate the offset so that the viewport
 * can account for them covering up part of the design
 */
function getContentOffset(
  sectionHeaderRef?: RefObject<TypeHeaderRefEl>,
  pageHeaderRef?: RefObject<HTMLElement | null>,
) {
  const sectionHeaderBoundingBox = sectionHeaderRef?.current?.getBoundingClientRect()
  const sectionHeaderHeight = sectionHeaderBoundingBox?.height ?? 0

  const pageHeaderBoundingBox = pageHeaderRef?.current?.getBoundingClientRect()
  const pageHeaderHeight = pageHeaderBoundingBox?.height ?? 0

  return sectionHeaderHeight + pageHeaderHeight
}

/**
 * Calculate the overlap between a section and the center zone of the viewport
 */
function calculateCenterOverlap(
  sectionTop: number,
  sectionBottom: number,
  centerZoneTop: number,
  centerZoneBottom: number,
): number {
  const overlapTop = Math.max(sectionTop, centerZoneTop)
  const overlapBottom = Math.min(sectionBottom, centerZoneBottom)
  return Math.max(0, overlapBottom - overlapTop)
}

/**
 * Returns activeId based on which section has the most area
 * overlapping with the center zone of the viewport
 */
function useCenterBasedActiveSection(tabsRef?: RefObject<TypeTabsRefEls>, rootMarginTop?: number) {
  const [activeId, setActiveId] = useState<string | undefined>()
  const activeIdRef = useRef<string | undefined>(undefined)

  // Keep ref in sync with state
  useEffect(() => {
    activeIdRef.current = activeId
  }, [activeId])

  const handleScroll = useCallback(() => {
    try {
      const elements = Object.entries(tabsRef?.current ?? {}).filter(([_, el]) => el) as [
        string,
        HTMLDivElement,
      ][]

      if (elements.length === 0) return

      const viewportHeight = window.innerHeight
      const rootOffset = rootMarginTop || 0

      // Calculate available viewport (excluding sticky headers)
      const availableViewport = viewportHeight - rootOffset

      // Define center zone as 40% of available viewport height, centered
      const centerZoneHeight = availableViewport * 0.4
      const centerZoneTop = rootOffset + (availableViewport - centerZoneHeight) / 2
      const centerZoneBottom = centerZoneTop + centerZoneHeight

      let maxOverlap = 0
      let newActiveId: string | undefined

      // Calculate overlap for each section
      for (const [id, element] of elements) {
        if (!element) continue

        const rect = element.getBoundingClientRect()
        const sectionTop = rect.top
        const sectionBottom = rect.bottom

        const overlap = calculateCenterOverlap(
          sectionTop,
          sectionBottom,
          centerZoneTop,
          centerZoneBottom,
        )

        if (overlap > maxOverlap) {
          maxOverlap = overlap
          newActiveId = id
        }
      }

      if (newActiveId && newActiveId !== activeIdRef.current) {
        setActiveId(newActiveId)
      }
    } catch (error) {
      // Log error but don't crash the app if DOM elements are unexpectedly removed
      console.warn('Error in scroll handler:', error)
    }
  }, [tabsRef, rootMarginTop])

  useEffect(() => {
    // Initial check
    handleScroll()

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [handleScroll])

  return activeId
}

function scrollToNavItemContent(
  id?: string,
  sectionHeaderRef?: RefObject<TypeHeaderRefEl>,
  pageHeaderRef?: RefObject<HTMLElement | null>,
  tabsRef?: RefObject<TypeTabsRefEls>,
) {
  if (!id) return

  const scrollY = window.pageYOffset || document.documentElement.scrollTop

  const contentItemEl = tabsRef?.current[id]
  const contentItemBoundingBox = contentItemEl?.getBoundingClientRect()
  const contentItemScrollPos = (contentItemBoundingBox?.top ?? 0) + scrollY

  window?.scrollTo({
    top: contentItemScrollPos - getContentOffset(sectionHeaderRef, pageHeaderRef),
    left: 0,
    behavior: 'smooth',
  })
}

export default function TabbedSection({ data }: { data?: TypeTabbedSectionData }) {
  const sectionHeaderRef = useRef<TypeHeaderRefEl>(null)
  const tabsRef = useRef<TypeTabsRefEls>({})
  const [contentOffset, setContentOffset] = useState(0)

  const { headerRef: pageHeaderRef } = usePageHeader()

  // Set scroll-margin-top on content sections for native browser scrolling
  const updateScrollMargins = useCallback(() => {
    try {
      if (typeof window === 'undefined') return // SSR safety check

      const offset = getContentOffset(sectionHeaderRef, pageHeaderRef)
      setContentOffset(offset)

      // Apply scroll-margin-top to all content sections
      for (const element of Object.values(tabsRef.current)) {
        if (element) {
          element.style.scrollMarginTop = `${offset}px`
        }
      }
    } catch (error) {
      // Log error but don't crash the app if DOM elements are unexpectedly removed
      console.warn('Error updating scroll margins:', error)
    }
  }, [pageHeaderRef])

  // Memoize the debounced version to prevent recreation on every render
  const debouncedUpdateScrollMargins = useMemo(() => {
    return debounce(updateScrollMargins, 150)
  }, [updateScrollMargins])

  // Set scroll-margin-top on content sections for native browser scrolling
  useEffect(() => {
    // Initial calculation
    updateScrollMargins()

    // Update on resize
    window.addEventListener('resize', debouncedUpdateScrollMargins, { passive: true })

    return () => {
      window.removeEventListener('resize', debouncedUpdateScrollMargins)
      // Cancel any pending debounced calls to prevent execution after unmount
      debouncedUpdateScrollMargins.cancel()
    }
  }, [updateScrollMargins, debouncedUpdateScrollMargins])

  const intersectingSectionId =
    useCenterBasedActiveSection(tabsRef, contentOffset) ?? data?.content?.[0]?.id

  return (
    <Section style={{ backgroundColor: 'var(--neutral-10)' }} className={styles.container}>
      <TabbedSectionHeader
        ref={sectionHeaderRef}
        data={data}
        selectedTabId={intersectingSectionId}
        onNavItemClick={id => scrollToNavItemContent(id, sectionHeaderRef, pageHeaderRef, tabsRef)}
      />
      <Column gap="9x" className={styles.content}>
        {data?.content &&
          data?.content?.length > 0 &&
          data?.content
            .filter(section => section?.id)
            .map((section, index) => (
              <div
                id={section.id}
                key={section.id}
                ref={el => {
                  tabsRef.current[section.id] = el
                }}
              >
                <TabbedSectionContent data={section} index={index} />
              </div>
            ))}
      </Column>
    </Section>
  )
}
